import type { FileTemplate } from "schemas/generated/fileTemplate";

import React from "react";

import { useInfiniteFileTemplates } from "@agent-ui/features/home/hooks/useInfiniteFileTemplates";
import ResponsiveControls from "@agent-ui/features/page-renderer/components/navbar/ResponsiveControls";
import { Empty } from "@replo/design-system/components/empty/Empty";
import { Input } from "@replo/design-system/components/input/Input";
import { PageTemplatePreviewModal } from "@v2/features/home/components/PageTemplatePreviewModal";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { FolderSearch, Search } from "lucide-react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDebouncedState } from "replo-utils/react/use-debounced-state";

import PageTemplateCard, { PageTemplateCardSkeleton } from "./PageTemplateCard";

const TEMPLATE_GRID_CONSTANTS = {
  MIN_CARD_WIDTH: 300,
} as const;

function TemplateListContent({
  emptySearchResults,
  globalTemplates,
  filteredTemplates,
  isLoadingGlobalTemplates,
  fetchNextGlobalPage,
  hasNextGlobalPage,
  onTemplateClick,
}: {
  emptySearchResults: boolean;
  globalTemplates: FileTemplate[];
  filteredTemplates: FileTemplate[];
  isLoadingGlobalTemplates: boolean;
  fetchNextGlobalPage: () => void;
  hasNextGlobalPage: boolean;
  onTemplateClick: (template: FileTemplate) => void;
}) {
  if (emptySearchResults) {
    return (
      <Empty
        title="No templates found"
        description="Try a different search term."
        icon={<FolderSearch />}
        UNSAFE_className="py-[10%]"
      />
    );
  }

  if (globalTemplates.length === 0 && !isLoadingGlobalTemplates) {
    return (
      <Empty
        title="No templates found"
        description="Check back later for new templates."
        icon={<FolderSearch />}
        UNSAFE_className="py-[10%]"
      />
    );
  }

  return (
    <InfiniteScroll
      dataLength={globalTemplates.length}
      next={() => void fetchNextGlobalPage()}
      hasMore={hasNextGlobalPage}
      loader={
        <div
          className="grid gap-4 mt-4 grid-cols-1"
          style={{
            gridTemplateColumns: `repeat(auto-fill, minmax(${TEMPLATE_GRID_CONSTANTS.MIN_CARD_WIDTH}px, 1fr))`,
          }}
        >
          {Array.from({ length: 4 }).map((_, index) => (
            <PageTemplateCardSkeleton key={`loader-${index}`} />
          ))}
        </div>
      }
      scrollableTarget="home-scroll-container"
    >
      <div
        className="grid gap-4 pb-6 grid-cols-1"
        style={{
          gridTemplateColumns: `repeat(auto-fill, minmax(${TEMPLATE_GRID_CONSTANTS.MIN_CARD_WIDTH}px, 1fr))`,
        }}
      >
        {isLoadingGlobalTemplates
          ? Array.from({ length: 8 }).map((_, index) => (
              <PageTemplateCardSkeleton key={`initial-${index}`} />
            ))
          : filteredTemplates.map((template) => (
              <PageTemplateCard
                key={template.id}
                template={template}
                onClick={() => onTemplateClick(template)}
              />
            ))}
      </div>
    </InfiniteScroll>
  );
}

function PageTemplatesSection() {
  const projectId = useCurrentProjectId();
  const [selectedTemplate, setSelectedTemplate] =
    React.useState<FileTemplate | null>(null);
  const [searchQueryInputValue, searchQuery, setSearchQuery] =
    useDebouncedState("");

  // Fallback to global templates if no project templates exist
  const {
    templates: globalTemplates,
    isLoading: isLoadingGlobalTemplates,
    fetchNextPage: fetchNextGlobalPage,
    hasNextPage: hasNextGlobalPage,
  } = useInfiniteFileTemplates({
    projectId: projectId ?? null,
    type: "page",
    limit: 8,
    onlyGlobalTemplates: true,
  });

  const filteredTemplates = React.useMemo(() => {
    if (!searchQuery) {
      return globalTemplates;
    }
    const lowerSearch = searchQuery.toLowerCase();
    return globalTemplates.filter((template) => {
      return template.name.toLowerCase().includes(lowerSearch);
    });
  }, [globalTemplates, searchQuery]);

  const handleTemplateClick = (template: FileTemplate) => {
    setSelectedTemplate(template);
  };

  const emptySearchResults =
    filteredTemplates.length === 0 &&
    !isLoadingGlobalTemplates &&
    searchQuery.length > 0;

  return (
    <>
      <div className="flex flex-col gap-4 h-full">
        <div className="flex items-center gap-3 w-full flex-shrink-0">
          <div className="flex min-w-0 w-full">
            <Input
              startEnhancer={<Search size={12} />}
              placeholder="Search"
              size="base"
              className="grow"
              value={searchQueryInputValue}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <ResponsiveControls variant="secondary" />
        </div>
        <TemplateListContent
          emptySearchResults={emptySearchResults}
          globalTemplates={globalTemplates}
          filteredTemplates={filteredTemplates}
          isLoadingGlobalTemplates={isLoadingGlobalTemplates}
          fetchNextGlobalPage={fetchNextGlobalPage}
          hasNextGlobalPage={hasNextGlobalPage}
          onTemplateClick={handleTemplateClick}
        />
      </div>
      {selectedTemplate && (
        <PageTemplatePreviewModal
          open
          onOpenChange={(open) => {
            if (!open) {
              setSelectedTemplate(null);
            }
          }}
          template={selectedTemplate}
          chatId=""
        />
      )}
    </>
  );
}

export default PageTemplatesSection;
